import { useState, useEffect } from "react";
import { GestionHeader } from "./SiteHeader";
import { apiGet, apiSend, errorMessage } from "./api";
import "./MailScenarios.css";

/* Même rendu que le backend (_render_scenario_message) : gabarit + réglages,
   avec l'accord du pluriel écrit en dur dans le gabarit. */
function renderMessage(template, params) {
  let msg = template;
  for (const p of params) {
    msg = msg.split(`{${p.key}}`).join(p.value);
  }
  return msg.replace(/\b1 (semaine|tampon)s/g, "1 $1");
}

/* Scénarios d'e-mails automatiques (plan Extra) : la liste et les bornes des
   réglages viennent du backend (registre MAIL_SCENARIOS) ; l'activation et
   les valeurs (réduction, validité…) se règlent ici. */
function MailScenarios({ user, onNavigate, onLogout }) {
  const [scenarios, setScenarios] = useState(null);
  const [error, setError] = useState("");
  const [forbidden, setForbidden] = useState(false);
  // Scénario en cours d'enregistrement : ses contrôles sont désactivés le
  // temps de l'aller-retour, pour éviter les doubles clics contradictoires.
  const [savingKey, setSavingKey] = useState(null);

  useEffect(() => {
    apiGet("/api/mail-scenarios")
      .then((data) => setScenarios(data.scenarios))
      .catch((e) => {
        if (e.status === 403) setForbidden(true);
        else setError(errorMessage(e, "Impossible de charger les scénarios"));
      });
  }, []);

  const patchScenario = (key, patch) =>
    setScenarios((prev) => prev.map((s) => (s.key === key ? { ...s, ...patch } : s)));

  const handleToggle = async (scenario) => {
    setSavingKey(scenario.key);
    setError("");
    const enabled = !scenario.enabled;
    try {
      await apiSend(`/api/mail-scenarios/${scenario.key}`, "PUT", { enabled },
        "Erreur lors de l'enregistrement");
      patchScenario(scenario.key, { enabled });
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de l'enregistrement"));
    }
    setSavingKey(null);
  };

  // La saisie reste locale (dirty) jusqu'à « Enregistrer » : le champ peut
  // passer par des états vides ou hors bornes sans déclencher d'appels.
  const handleParamChange = (scenario, paramKey, raw) => {
    patchScenario(scenario.key, {
      dirty: true,
      params: scenario.params.map((p) =>
        p.key === paramKey ? { ...p, value: raw } : p),
    });
  };

  const handleSaveParams = async (scenario) => {
    const params = {};
    for (const p of scenario.params) {
      if (p.type === "choice") {
        params[p.key] = p.value;
      } else if (p.type === "text") {
        const v = String(p.value);
        if (v.trim() === "" || v.length > p.max_len) {
          setError(`« ${p.label} » doit être un texte non vide de ${p.max_len} caractères au plus`);
          return;
        }
        params[p.key] = v;
      } else {
        const v = parseInt(p.value, 10);
        if (isNaN(v) || v < p.min || v > p.max) {
          setError(`« ${p.label} » doit être un entier entre ${p.min} et ${p.max}`);
          return;
        }
        params[p.key] = v;
      }
    }
    setSavingKey(scenario.key);
    setError("");
    try {
      const data = await apiSend(`/api/mail-scenarios/${scenario.key}`, "PUT",
        { params }, "Erreur lors de l'enregistrement");
      patchScenario(scenario.key, {
        dirty: false,
        params: data.params,
        message: data.message,
      });
    } catch (e) {
      setError(errorMessage(e, "Erreur lors de l'enregistrement"));
    }
    setSavingKey(null);
  };

  return (
    <div className="scen-container">
      <GestionHeader
        user={user}
        onNavigate={onNavigate}
        title="Scénarios d'e-mails"
        onBack={() => onNavigate("home")}
        onHome={() => onNavigate("home")}
        onLogout={onLogout}
      />
      <main className="scen-main">
        {forbidden || !user.organization_id ? (
          <p className="scen-error">
            {forbidden
              ? "Les scénarios d'e-mails sont réservés au plan Extra."
              : "Aucune organisation associée à votre compte."}
          </p>
        ) : (
          <>
            <h2>Vos scénarios</h2>
            <p className="scen-intro">
              Cochez un scénario pour l'activer : vos clients recevront
              automatiquement l'e-mail correspondant, ainsi qu'une
              notification sur leur carte Apple Wallet ou Google Wallet
              (sauf le scénario Promotion, qui n'envoie que des e-mails).
            </p>
            {error && <p className="scen-error">{error}</p>}
            {scenarios === null && !error && <p className="scen-loading">Chargement…</p>}
            <div className="scen-list">
            {scenarios && scenarios.map((sc) => (
              <div
                key={sc.key}
                className={`scen-card${sc.enabled ? " enabled" : ""}${sc.available ? "" : " unavailable"}`}
              >
                <label className="scen-head">
                  <input
                    type="checkbox"
                    checked={sc.enabled}
                    disabled={savingKey === sc.key || !sc.available}
                    onChange={() => handleToggle(sc)}
                  />
                  <span className="scen-name">{sc.name}</span>
                  <span className={`scen-state${sc.enabled ? " on" : ""}`}>
                    {!sc.available ? "Indisponible" : sc.enabled ? "Activé" : "Désactivé"}
                  </span>
                </label>
                <div className="scen-body">
                  <span className="scen-trigger">
                    Déclencheur : {sc.trigger}
                  </span>
                  {sc.note && <span className="scen-note">{sc.note}</span>}
                  <div className="scen-params">
                    {sc.key === "custom" ? (
                      /* Promotion : la configuration se lit comme une phrase
                         à trous — « Si …, le client reçoit le mail … après
                         X jours. » Les trous sont branchés sur les mêmes
                         réglages (trigger / text / days) que l'API. */
                      (() => {
                        const trigger = sc.params.find((p) => p.key === "trigger");
                        const days = sc.params.find((p) => p.key === "days");
                        const text = sc.params.find((p) => p.key === "text");
                        const isBirthday = trigger.value === "birthday";
                        return (
                          <div className="scen-madlib">
                            <span>Si</span>
                            <select
                              className="scen-madlib-blank"
                              value={trigger.value}
                              disabled={savingKey === sc.key}
                              onChange={(e) => handleParamChange(sc, "trigger", e.target.value)}
                            >
                              {trigger.options.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </select>
                            <span>, le client reçoit le mail</span>
                            <textarea
                              className="scen-madlib-text"
                              rows={3}
                              maxLength={text.max_len}
                              placeholder="votre message…"
                              value={text.value}
                              disabled={savingKey === sc.key}
                              onChange={(e) => handleParamChange(sc, "text", e.target.value)}
                            />
                            {isBirthday ? (
                              <span>le jour même.</span>
                            ) : (
                              <>
                                <span>après</span>
                                <input
                                  type="number"
                                  className="scen-madlib-days"
                                  min={days.min}
                                  max={days.max}
                                  value={days.value}
                                  disabled={savingKey === sc.key}
                                  onChange={(e) => handleParamChange(sc, "days", e.target.value)}
                                />
                                <span>jour(s).</span>
                              </>
                            )}
                          </div>
                        );
                      })()
                    ) : sc.params.map((p) => (
                      <label key={p.key} className="scen-param">
                        <span>{p.label}</span>
                        <input
                          type="number"
                          min={p.min}
                          max={p.max}
                          value={p.value}
                          disabled={savingKey === sc.key}
                          onChange={(e) => handleParamChange(sc, p.key, e.target.value)}
                        />
                      </label>
                    ))}
                    {sc.dirty && (
                      <button
                        className="scen-save-btn"
                        disabled={savingKey === sc.key}
                        onClick={() => handleSaveParams(sc)}
                      >
                        {savingKey === sc.key ? "Enregistrement…" : "Enregistrer"}
                      </button>
                    )}
                  </div>
                  {/* Pas d'aperçu pour la Promotion : le message est déjà
                      saisi tel quel dans la phrase à trous. */}
                  {sc.key !== "custom" && (
                    <span className="scen-message">
                      {renderMessage(sc.template, sc.params)}
                    </span>
                  )}
                </div>
              </div>
            ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}

export default MailScenarios;
